/*
  Life Inbox input — the signature capture experience.
  One text input, one CTA. Submission classifies via Gemini 3 Flash, shows
  a compact review card, lets the user tweak the type before confirming.
*/
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Send, Check, X, Pill, CheckSquare, ShoppingCart, StickyNote,
  Bell, CalendarDays, Loader2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { inboxApi } from "../lib/inboxApi";
import { trackEvent } from "../lib/analytics";
import VoiceCaptureButton from "./VoiceCaptureButton";
import { isFeatureEnabled } from "./FeatureGate";

const TYPE_META = {
  task:         { label: "To-do",        icon: CheckSquare,  hint: "Goes to your To-Do list" },
  reminder:     { label: "Reminder",     icon: Bell,         hint: "Scheduled alert" },
  shopping:     { label: "Shopping",     icon: ShoppingCart, hint: "Goes to your Shopping list" },
  note:         { label: "Note",         icon: StickyNote,   hint: "Saved to Notes" },
  prescription: { label: "Prescription", icon: Pill,         hint: "Opens prescription setup" },
  event:        { label: "Event",        icon: CalendarDays, hint: "Time-anchored event" },
};

const EXAMPLES = [
  "remind me to pick up paracetamol tomorrow at 5pm",
  "need milk and eggs on the way home",
  "dentist appt friday 10am",
  "idea: add mileage heatmap by hour",
];

export default function LifeInboxInput({ compact = false }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const nav = useNavigate();

  async function submit(e) {
    e?.preventDefault?.();
    const input = value.trim();
    if (!input || busy) return;
    setBusy(true);
    try {
      const res = await inboxApi.classify(input);
      setReview(res);
      trackEvent("life_inbox_classified", { type: res.type, confidence: res.confidence });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't classify — try rephrasing");
    } finally {
      setBusy(false);
    }
  }

  function pickExample(txt) {
    setValue(txt);
  }

  async function confirm() {
    setBusy(true);
    try {
      await inboxApi.capture({
        original: review.original,
        type: review.type,
        title: review.title,
        datetime: review.datetime,
        is_recurring: review.is_recurring,
        recurrence: review.recurrence,
        priority: review.priority,
        tags: review.tags,
        confidence: review.confidence,
        status: "new",
      });
      trackEvent("life_inbox_captured", { type: review.type });
      toast.success("Captured");
      setValue("");
      setReview(null);
      // Route to the appropriate tool entry point
      const go = {
        task: "/todo",
        reminder: "/inbox",
        shopping: "/shopping",
        note: "/notes",
        prescription: "/prescriptions/new",
        event: "/inbox",
      }[review.type] || "/inbox";
      nav(go);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  function changeType(next) {
    setReview(r => ({ ...r, type: next }));
  }

  function reset() {
    setReview(null);
    setValue("");
  }

  return (
    <div className={compact ? "" : "card-tactile rounded-2xl p-5 sm:p-7"} data-testid="life-inbox">
      {!compact && (
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-[color:var(--primary-ink)]" />
          </div>
          <div>
            <div className="label-caps">Life Inbox</div>
            <h2 className="font-display font-bold text-lg tracking-tight">Capture anything · we'll sort it.</h2>
          </div>
        </div>
      )}

      {!review ? (
        <form onSubmit={submit} className="space-y-3">
          <div className="flex items-center gap-2 rounded-xl border px-3 h-12 transition-colors focus-within:border-[color:var(--primary)]"
            style={{ borderColor: "var(--border-subtle)", background: "var(--surface)" }}>
            <Sparkles className="h-4 w-4 shrink-0" style={{ color: "var(--text-muted)" }} />
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Type a thought, a task, a reminder…"
              data-testid="life-inbox-input"
              className="flex-1 bg-transparent outline-none text-sm"
              autoComplete="off"
              disabled={busy}
            />
            {isFeatureEnabled("feature_voice_capture") && (
              <VoiceCaptureButton
                disabled={busy}
                onInterim={(t) => setValue(t)}
                onResult={(t) => {
                  setValue(t);
                  trackEvent("life_inbox_voice_used");
                  // Auto-submit short final transcripts so the flow feels truly hands-free
                  if (t && t.length < 200) {
                    setTimeout(() => {
                      // Use a synthesized event; actual submit() is wrapped in form onSubmit
                      const form = document.querySelector('[data-testid="life-inbox"] form');
                      form?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
                    }, 250);
                  }
                }}
              />
            )}
            <button type="submit" disabled={busy || !value.trim()}
              data-testid="life-inbox-submit"
              className="h-9 px-3 rounded-lg text-sm font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-40 transition-opacity"
              style={{ background: "var(--primary)" }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              <span className="hidden sm:inline">Sort</span>
            </button>
          </div>
          {!compact && (
            <div className="flex flex-wrap gap-1.5">
              <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>Try:</span>
              {EXAMPLES.map(ex => (
                <button type="button" key={ex} onClick={() => pickExample(ex)}
                  data-testid="life-inbox-example"
                  className="text-[11px] px-2 py-0.5 rounded-full border hover:border-[color:var(--primary)] transition-colors"
                  style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}>
                  {ex}
                </button>
              ))}
            </div>
          )}
        </form>
      ) : (
        <ReviewCard review={review} onConfirm={confirm} onReset={reset} onChangeType={changeType} busy={busy} />
      )}
    </div>
  );
}

function ReviewCard({ review, onConfirm, onReset, onChangeType, busy }) {
  const meta = TYPE_META[review.type] || TYPE_META.task;
  const Icon = meta.icon;
  const when = review.datetime ? new Date(review.datetime).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null;
  return (
    <div data-testid="life-inbox-review" className="space-y-3">
      <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", background: "var(--surface-2)" }}>
        <div className="text-[11px] italic mb-2" style={{ color: "var(--text-muted)" }}>"{review.original}"</div>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg shrink-0 bg-[color:var(--primary-soft)] flex items-center justify-center">
            <Icon className="h-5 w-5 text-[color:var(--primary-ink)]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--primary-ink)" }}>{meta.label}</span>
              {review.confidence >= 0.6 && (
                <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{Math.round(review.confidence * 100)}% sure</span>
              )}
              {review.priority && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}>{review.priority}</span>
              )}
            </div>
            <div className="font-display font-bold text-base mt-0.5 truncate">{review.title}</div>
            {when && <div className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><CalendarDays className="h-3 w-3" /> {when}</div>}
            {review.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {review.tags.map(t => (
                  <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full"
                    style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}>#{t}</span>
                ))}
              </div>
            )}
            <div className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>{meta.hint}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="label-caps mb-1.5">Not right? Change type</div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(TYPE_META).map(([k, m]) => (
            <button key={k} onClick={() => onChangeType(k)} data-testid={`life-inbox-type-${k}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${review.type === k ? "text-white border-transparent" : "hover:border-[color:var(--primary)]"}`}
              style={review.type === k ? { background: "var(--primary)" } : { borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            >{m.label}</button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={onConfirm} disabled={busy} data-testid="life-inbox-confirm"
          className="flex-1 h-10 rounded-lg font-semibold text-sm text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          style={{ background: "var(--primary)" }}>
          <Check className="h-4 w-4" /> Save & open
        </button>
        <button onClick={onReset} disabled={busy} data-testid="life-inbox-reset"
          className="h-10 px-3 rounded-lg font-semibold text-sm border inline-flex items-center gap-1.5 hover:bg-[color:var(--surface-2)]"
          style={{ borderColor: "var(--border-subtle)" }}>
          <RotateCcw className="h-4 w-4" /> Restart
        </button>
      </div>
    </div>
  );
}
